import Grid from "@mui/material/Grid";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "custom/LayoutContainers/DashboardLayout";
import {useCallback, useEffect, useState} from "react";
import {createStaff, deleteStaff, getAllStaff, getStaffById, updateStaff} from "../../api/staff/staffApiClient";
import DeleteConfirmDialog from "./components/DeleteConfirmDialog";
import UserFormDialog from "../../components/UserFormDialog";
import {getAllBranches} from "../../api/branch/branchApiClient";
import {STAFF_DESIGNATION_SELECT_OPTIONS} from "../../utils/formConstants";
import {validateBranch, validateDesignation} from "../../utils/formValidations";
import SoftButton from "components/SoftButton";
import SoftInput from "../../components/SoftInput";
import {Outlet, useLocation, useNavigate} from "react-router-dom";
import {IconButton} from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {Delete as DeleteIcon, Edit as EditIcon, Visibility as VisibilityIcon} from "@mui/icons-material";
import EnhancedTable from "../../components/EnhancedTable";
import DataTable, {renderBase64ImageCell, renderPill} from "../../components/DataTable";
import {getStatusColor, getStatusDisplayName, getUserRoleColor, getRoleDisplayName} from "../../utils/statusUtils";
import {handleApiError, showDeleteSuccess} from "../../utils/notificationUtils";

function Overview() {
    const [staff, setStaff] = useState([]);
    const [filteredStaff, setFilteredStaff] = useState([]);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [staffToDelete, setStaffToDelete] = useState(null);
    const [staffToEdit, setStaffToEdit] = useState(null);
    const [editAddDialogOpen, setEditAddDialogOpen] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    const isOnChildRoute = location.pathname.includes('/staff/') && location.pathname !== '/staff';

    const columns = [
        {
            name: "profile Pic",
            value: "profilePic",
            align: "center",
            width: "10",
            renderCell: (row) => renderBase64ImageCell(row, "profilePic"),
        },
        {name: "first Name", value: "firstName", align: "left", width: "10"},
        {name: "Last Name", value: "lastName", align: "left", width: "10"},
        {name: "email", value: "email", align: "left", width: "20"},
        {name: "phone No", value: "phoneNumber", align: "left", width: "10"},
        {
            name: "position",
            value: "userRole",
            align: "center",
            width: "10",
            renderCell: (row) => renderPill(getRoleDisplayName(row.userRole), getUserRoleColor(row.userRole)),
        },
        {
            name: "designation",
            value: "designation",
            align: "left",
            width: "10",
            renderCell: (row) => (
                <SoftTypography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                    sx={{display: "inline-block", width: "max-content"}}
                >
                    {row.designation?.replace("_", " ")}
                </SoftTypography>
            ),
        },
        {
            name: "status",
            value: "status",
            align: "center",
            width: "10",
            renderCell: (row) => renderPill(getStatusDisplayName(row.status), getStatusColor(row.status)),
        },
        {
            name: "Actions",
            value: "action",
            align: "center",
            width: "10",
            renderCell: (row) => (
                <>
                    <IconButton color="primary" size="small" title="View Details" onClick={() => handleViewClick(row)}>
                        <VisibilityIcon/>
                    </IconButton>
                    <IconButton color="primary" size="small" title="Edit" onClick={() => handleEditClick(row)}>
                        <EditIcon/>
                    </IconButton>
                    <IconButton color="error" size="small" title="Delete" onClick={() => handleDeleteClick(row)}>
                        <DeleteIcon/>
                    </IconButton>
                </>
            ),
        },
    ];

    const loadStaff = useCallback(async () => {
        try {
            setIsLoading(true);
            
            const response = await getAllStaff();
            console.log('Staff API Response:', response);
            console.log('Staff API Response Data:', response.data);

            // Handle different response structures
            let staffData = [];
            if (response && response.data) {
                if (Array.isArray(response.data)) {
                    staffData = response.data;
                } else if (response.data.content && Array.isArray(response.data.content)) {
                    staffData = response.data.content;
                } else if (response.data.staff && Array.isArray(response.data.staff)) {
                    staffData = response.data.staff;
                } else {
                    console.warn('Unexpected API response structure:', response.data);
                    staffData = [];
                }
            }

            console.log('Processed staff data:', staffData);
            setStaff(staffData);
            setFilteredStaff(staffData);
        } catch (error) {
            console.error('Error loading staff:', error);
            handleApiError(error, 'Failed to load staff. Please try again.');
            // Set empty arrays on error
            setStaff([]);
            setFilteredStaff([]);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadStaff().then();
    }, [loadStaff]);

    // Add effect to refresh data when returning from detail view
    useEffect(() => {
        // If we're on the main staff page (not a child route), refresh the data
        if (!isOnChildRoute) {
            console.log('Returned to staff table view, refreshing data...');
            loadStaff();
        }
    }, [location.pathname, isOnChildRoute, loadStaff]);

    const handleStaffAdded = async (formData) => {
        try {
            await createStaff(formData);
            await loadStaff();
        } catch (error) {
            throw error;
        }

    };

    const handleDeleteClick = (staff) => {
        setStaffToDelete(staff);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            await deleteStaff(staffToDelete.id);
            await loadStaff();
            setDeleteDialogOpen(false);
            setStaffToDelete(null);
            showDeleteSuccess('Staff');
        } catch (err) {
            handleApiError(err, 'Failed to delete staff');
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setStaffToDelete(null);
    };

    const handleEditClick = async (staff) => {
        try {
            // Fetch fresh staff data from backend to get updated images
            const freshStaffResponse = await getStaffById(staff.id);
            const freshStaffData = freshStaffResponse.data;
            
            console.log('Fresh staff data for edit:', freshStaffData);
            
            setStaffToEdit(freshStaffData);
            setIsEdit(true);
            setEditAddDialogOpen(true);
        } catch (error) {
            console.error('Error fetching fresh staff data for edit:', error);
            // Fallback to using cached data if fresh fetch fails
            setStaffToEdit(staff);
            setIsEdit(true);
            setEditAddDialogOpen(true);
        }
    };

    const handleViewClick = (staff) => {
        navigate(`/staff/${staff.id}`);
    };

    const handleEditStaff = async (formData) => {
        try {
            await updateStaff(staffToEdit.id, formData);
            await loadStaff();
        } catch (error) {
            throw error;
        }
    };
    const handleOpenAddDialog = () => {
        setStaffToEdit(null);
        setIsEdit(false);
        setEditAddDialogOpen(true);
    }
    const handleCloseDialog = () => {
        setDeleteDialogOpen(false)
        setEditAddDialogOpen(false);
        setStaffToEdit(null);
        setStaffToDelete(null);
    }

    const handleFilterStaff = (value) => {
        let filtered = staff.filter(staffMember => {
            return staffMember.firstName.toLowerCase().includes(value.toLowerCase()) ||
                staffMember.lastName.toLowerCase().includes(value.toLowerCase()) ||
                staffMember.email.toLowerCase().includes(value.toLowerCase()) ||
                staffMember.phoneNumber.toLowerCase().includes(value.toLowerCase());
        });
        setFilteredStaff(filtered);
    }

    async function handleOnSubmit(formData) {
        if (isEdit) {
            await handleEditStaff(formData);
        } else {
            await handleStaffAdded(formData);
        }
    }

    return (
        <DashboardLayout>
            <SoftBox pt={2} px={2} width="100%">
                <SoftBox pt={2} px={2} display="flex" alignItems="center" justifyContent="space-between" width="100%" gap={2}>
                    <SoftBox display="flex" alignItems="center" gap={2}>
                        {isOnChildRoute && (
                            <IconButton
                                onClick={() => navigate('/staff')}
                                sx={{
                                    backgroundColor: 'primary.main',
                                    color: 'white',
                                    '&:hover': {
                                        backgroundColor: 'primary.dark',
                                    }
                                }}
                            >
                                <ArrowBackIcon/>
                            </IconButton>
                        )}
                        <SoftTypography variant="h6" fontWeight="medium">
                            {isOnChildRoute ? 'Staff Details' : 'Staff'}
                        </SoftTypography>
                    </SoftBox>
                    {!isOnChildRoute && (
                        <>
                            <SoftBox flexGrow={1} maxWidth={560}>
                                <SoftInput
                                    placeholder="Type here to filter by name, email or phone"
                                    icon={{component: "search", direction: "left"}}
                                    onChange={(event) => handleFilterStaff(event.target.value)}
                                />
                            </SoftBox>
                            <SoftButton color="primary" onClick={handleOpenAddDialog} sx={{flexShrink: 0}}>
                                Add Staff
                            </SoftButton>
                        </>
                    )}
                </SoftBox>
            </SoftBox>
            <SoftBox p={2} width="100%">
                {isOnChildRoute ? (
                    <Outlet/>
                ) : (
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <EnhancedTable
                                TableComponent={DataTable}
                                isLoading={isLoading}
                                data={filteredStaff}
                                loadingMessage="Loading staff..."
                                noDataMessage="No staff found"
                                columns={columns}
                            />
                        </Grid>
                    </Grid>
                )}
            </SoftBox>
            <DeleteConfirmDialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                onDelete={handleDeleteConfirm}
                warningMessage={`Are you sure you want to delete ${staffToDelete?.firstName || ""} ${staffToDelete?.lastName || ""}? This action cannot be undone.`}
            />
            <UserFormDialog
                open={editAddDialogOpen}
                onClose={handleCloseDialog}
                onSubmit={handleOnSubmit}
                user={staffToEdit}
                isEdit={isEdit}
                entityLabel="Staff"
                defaultUserRole="STAFF"
                minAge={18}
                extraFields={[
                    {
                        key: "branchId",
                        label: "Branch",
                        type: "asyncSelect",
                        loader: getAllBranches,
                        getValue: (b) => b.branchId,
                        getLabel: (b) => b.location,
                        required: true,
                        validate: validateBranch,
                        span: 6,
                    },
                    {
                        key: "designation",
                        label: "Designation",
                        type: "select",
                        options: STAFF_DESIGNATION_SELECT_OPTIONS,
                        required: true,
                        validate: validateDesignation,
                        span: 6,
                    },
                ]}
            />
        </DashboardLayout>
    );
}

export default Overview;
